import { z } from 'zod';
import { DifficultySchema } from '../enums/difficulty.js';
import { FlashcardTypeSchema } from '../enums/flashcard-type.js';
import { CognitiveLevelSchema } from './cognitive-level.js';
import { ChunkRefSchema } from './source-ref.js';
import { ItemTagsSchema, coerceItemTagsV3ToV4 } from './item-tags.js';
import { ConceptOmissionSchema } from './concept-omission.js';
// Session 19AE: full .describe() coverage for LLM prompt clarity. Zod's JSON
// Schema export carries description through, which is what the LLM reads via
// `wrapSystemPromptWithSchema` in openai-compat provider.
//
// id formats: `fc1_ramayana_rate` (IB), `bf_01` (Brand topic-prefixed),
// `fc_<chapter>_<n>` (CS). All accepted via the snake_case regex.
//
// 19BH Day 2 Step 2.4: ItemTags base + omittedConcepts soft escape. Singular
// caseId/topicId/conceptTag retained as deprecated optional aliases.
export const FlashcardSchema = z.preprocess(coerceItemTagsV3ToV4, ItemTagsSchema.extend({
    id: z.string().regex(/^[a-z][a-z0-9_]+$/, 'flashcard id must be lowercase snake_case')
        .describe('Unique snake_case slug. Convention: `fc_<chapterSlug>_<n>` (per-chapter) or `fc_general_<n>` (cross-case).'),
    /** @deprecated v3 alias of caseIds[0]. */
    caseId: z.string().nullable().optional()
        .describe('@deprecated v3 alias of caseIds[0]. New code should read caseIds[].'),
    /** @deprecated v3 alias of topicIds[0]. */
    topicId: z.string().nullable().optional()
        .describe('@deprecated v3 alias of topicIds[0].'),
    subtopicId: z.string().nullable().optional()
        .describe('Subtopic id with topic prefix. Most-specific anchor when applicable. null/omit if not applicable.'),
    section: z.string().nullable()
        .describe('Sub-section label within the case (e.g. "rare_earth" / "nafta" for split cases). null for unsplit.'),
    type: FlashcardTypeSchema
        .describe('Flashcard category — one of fact / framework / concept / etc. Drives sorting + study-mode targeting.'),
    difficulty: DifficultySchema
        .describe('easy / medium / hard. Drives default review-order surfacing.'),
    cognitiveLevel: CognitiveLevelSchema.optional()
        .describe('Bloom 5-tier level (recall / understand / apply / analyze / evaluate).'),
    front: z.string().min(15)
        .describe('Question / prompt side. ≥15 chars (a meaningful prompt · not "X?"). Concise (≤200 chars). Markdown allowed.'),
    back: z.string().min(40)
        .describe('Answer / reveal side. ≥40 chars (a real answer · not "Yes" / "5"). Markdown REQUIRED for any pedagogical content beyond a single fact: lead with **bold** key term, support with bullet list or 2-3 short paragraphs. Inline math `$...$`, blocks `$$...$$`. Avoid wall-of-text — prefer scannable structure. Constraint Session 19AO.'),
    tags: z.array(z.string())
        .describe('Free-form tags for filtering (e.g. ["framework", "REACH", "lobbying"]).'),
    relatedConcepts: z.array(z.string())
        .describe('Glossary term ids OR concept names this card touches. UI may render as chips.'),
    relatedCards: z.array(z.string())
        .describe('Other flashcard ids semantically related. Optional cross-link for spaced-repetition graph.'),
    /** @deprecated v3 alias of conceptIds[0]. */
    conceptTag: z.string().min(1).max(80).nullish()
        .describe('@deprecated v3 alias of conceptIds[0]. New code should read conceptIds[].'),
    sourceRef: ChunkRefSchema.nullish()
        .describe('19BG+2 Phase 5 (DEBT-AW-SOURCEREF): citation anchor. Snippet substring-verified against source markdown by the source-ref lint rule. Drives fictional metric. Optional for back-compat.'),
    omittedConcepts: z.array(ConceptOmissionSchema).nullish()
        .describe('K18 soft escape · concepts the LLM declared materially insufficient for this stage.'),
}).passthrough());
export const FlashcardsSchema = z.array(FlashcardSchema);
//# sourceMappingURL=flashcard.js.map